import React, { useState } from 'react';
import styled from 'styled-components';

import dayjs from 'dayjs';
import { DownChevron, UpChevron, SGP } from '@williamhill/rosi-ui-shared/src/assets/icons';
import FooterComponent from './footer-component';
import LegsComponent, { Leg } from './legs-component';
import { BetStatusType } from '@williamhill/rosi-ui-shared/src/types';
import { getBetStatus } from '@williamhill/rosi-ui-shared/src/lib/bet-status';
import { removePipes, withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import { unitsValueMoney } from '@williamhill/rosi-ui-shared/src/lib/money';
import { TicketView } from '../../services';
import { Text, Block, DisplayHeading } from '@williamhill/rosi-ui-shared/src/components/typography';
import { BetLeg } from '../../model';

const withTestId = withRootTestId('bet-tracker-bets-ticket');

export default function TicketComponent(ticket: TicketView & { isSelected: boolean }) {
  const [legsVisible, setLegsVisible] = useState(false);

  const { legs, payout, typeName, parlayCardEditionId, betLegs, isSelected } = ticket;
  const showInjuryProtectionMessage = legs.some(leg => leg.result?.type === 'injury_protection');
  const showVoidedAdjustmentMessage = !showInjuryProtectionMessage && ticket.showVoidedAdjustmentMessage;
  const statusIcon = statusIcons[getBetStatus({ ...ticket, payout: unitsValueMoney(payout || 0) })];
  const hideOdds = parlayCardEditionId != null;
  const isSGP = 'Same Game Parlay'.includes(typeName);
  const isParlay = !isSGP && !betLegs && legs.length > 1;

  const spLegLength = (type: string) => {
    return betLegs?.filter(betLeg => {
      return betLeg.type === type;
    }).length;
  };

  const onLegsToggle = () => setLegsVisible(v => !v);

  return (
    <BetSection data-testid={withTestId('section')} isSelected={isSelected}>
      <Text as="header" weight="bold" size="md" data-testid={withTestId('header')}>
        {statusIcon && <StatusIconContainer>{statusIcon}</StatusIconContainer>}
        <TitleComponent {...{ isParlay, isSGP, legs, betLegs, typeName, onLegsToggle }} areLegsVisible={legsVisible} />
      </Text>
      <main>
        <BetSummary size="sm">
          {!isParlay && (
            <>
              <EventNamesContainer data-testid={withTestId('event-name')}>
                <EventNameSpan data-testid={withTestId('event-name')}>
                  {isSGP && (
                    <>
                      {dayjs(legs[0].eventStartTime).format('MMM DD, h:mma')} | {removePipes(legs[0].event.name)}
                    </>
                  )}
                  {legs.length === 1 && legs.map(({ event }) => removePipes(event.name)).join(', ')}
                  {betLegs && (
                    <>
                      Contains {spLegLength('leg-group')} Same Game Parlay and {spLegLength('standard')} Straight
                      Selections
                    </>
                  )}
                </EventNameSpan>
              </EventNamesContainer>
              <LegsToggleComponent {...{ onLegsToggle }} areLegsVisible={legsVisible} />
            </>
          )}
        </BetSummary>
        {legsVisible && <LegsComponent {...{ legs, betLegs, hidePriceOnLegs: hideOdds, isSGP }} />}
      </main>
      <FooterComponent {...{ ...ticket, hideOdds, showInjuryProtectionMessage, showVoidedAdjustmentMessage }} />
    </BetSection>
  );
}

const BetSection = styled.section<{ isSelected: boolean }>`
  background-color: ${({ theme }) => theme.colors.white};
  padding: ${({ isSelected }) => (isSelected ? '1.4rem 1.4rem 0.6rem 1.4rem' : '1.6rem 1.6rem 0.8rem 1.6rem')};
  ${({ theme, isSelected }) => isSelected && `border: solid 0.2rem ${theme.colors.modals.success};`}

  & hr {
    border-style: none;
    border-top: 1px solid ${({ theme }) => theme.colors.grey200};
    height: 1px;
    margin: 0.8rem 0;
  }
`;

const StatusIconContainer = styled.div`
  margin-bottom: 0.6rem;
`;

const StatusIconSpan = styled(Text).attrs(() => ({ ['data-testid']: withTestId('status-icon') }))<{
  background: 'black' | 'green' | 'neutral' | 'red';
}>`
  background-color: ${({ background, theme }) => theme.colors[background]};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.grey100};
  display: inline-block;
  line-height: 1.5rem;
  padding: 0 0.4rem;
`;

const WonIcon = (
  <StatusIconSpan weight="regular" size="sm" background="green">
    WON
  </StatusIconSpan>
);

const statusIcons: Partial<Record<BetStatusType, JSX.Element>> = {
  expired: (
    <StatusIconSpan weight="regular" size="sm" background="neutral">
      EXPIRED
    </StatusIconSpan>
  ),
  lost: (
    <StatusIconSpan weight="regular" size="sm" background="red">
      LOST
    </StatusIconSpan>
  ),
  paid: WonIcon,
  payout: WonIcon,
  payoutTaxReportable: WonIcon,
  payoutTaxWithholdings: WonIcon,
  payoutWithChildSupportCheck: WonIcon,
  void: (
    <StatusIconSpan weight="regular" size="sm" background="black">
      VOID
    </StatusIconSpan>
  ),
  voidCashed: (
    <StatusIconSpan weight="regular" size="sm" background="black">
      VOID
    </StatusIconSpan>
  )
};

const BetSummary = styled(Block)`
  color: ${({ theme }) => theme.colors.brand.quaternary};
  display: flex;
  padding-bottom: 0.8rem;
`;

export const EventNamesContainer = styled.div`
  flex-grow: 1;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const LegsToggleContainer = styled.div`
  display: flex;
`;

const EventNameSpan = styled.span`
  color: ${({ theme }) => theme.colors.brand.quaternary};
`;

export const SGPWrapper = styled.div<{ isSuperParlay?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex: 1;
  margin-right: 0.5rem;

  ${({ isSuperParlay }) =>
    isSuperParlay &&
    `
    margin-right: 0;
    margin-left: 0.5rem;
  `}
`;

const Title = styled(DisplayHeading).attrs({ as: 'header', size: 'xs', weight: 'bold' })<{ isParlay?: boolean }>`
  text-transform: uppercase;
  ${({ isParlay = false }) =>
    isParlay &&
    `
    display: flex;
    justify-content: space-between;
  `}
  > span {
    color: ${({ theme }) => theme.colors.brand.sgp};
  }
`;

const TitleComponent = ({
  legs,
  isSGP,
  isParlay,
  onLegsToggle,
  betLegs,
  typeName,
  areLegsVisible
}: {
  isSGP: boolean;
  isParlay: boolean;
  legs: Leg[];
  betLegs?: BetLeg[];
  typeName: string;
  onLegsToggle: () => void;
  areLegsVisible: boolean;
}) => {
  if (isSGP)
    return (
      <Title>
        <SGPWrapper>
          <SGP width="2rem" border={false} />
        </SGPWrapper>
        {legs.length} LEG {typeName}
      </Title>
    );
  if (isParlay)
    return (
      <Title isParlay>
        {typeName}
        <LegsToggleComponent {...{ areLegsVisible, onLegsToggle }} />
      </Title>
    );
  if (betLegs)
    return (
      <Title>
        {legs.length} LEG <span>SUPER PARLAY</span>
      </Title>
    );
  return <>{typeName}</>;
};

const LegsToggleComponent = ({
  onLegsToggle,
  areLegsVisible
}: {
  onLegsToggle: () => void;
  areLegsVisible: boolean;
}) => {
  return (
    <LegsToggleContainer data-testid={withTestId('legs-toggle')} onClick={() => onLegsToggle()}>
      {areLegsVisible ? (
        <UpChevron data-testid={withTestId('legs-toggle-up-chevron')} width="1.4rem" />
      ) : (
        <DownChevron data-testid={withTestId('legs-toggle-down-chevron')} width="1.4rem" />
      )}
    </LegsToggleContainer>
  );
};
